import { SUBSCRIPTION_CONFIG } from '@hero/core';
import {
  useBillingListApi,
  useFeaturesMineApi,
  useSubscriptionMineApi,
} from '@hero/api-hooks';
import { useNav } from '../navigation';
import { useBillingPayManager } from './useBillingPayManager';

export type BillingOverviewViewMode =
  | 'loading'
  | 'landing'
  | 'overview'
  | 'paywall';

/**
 * Aggregator for BillingOverviewPage — data, view mode, and pay-now actions.
 */
export function useBillingOverviewManager() {
  const subscriptionQuery = useSubscriptionMineApi();
  const featuresQuery = useFeaturesMineApi();
  const billingListQuery = useBillingListApi({});
  const { navigate } = useNav();
  const { payLatestUnpaid } = useBillingPayManager();

  const subscriptionData = subscriptionQuery.data;
  const subscription = subscriptionData ?? null;
  const features = featuresQuery.data ?? null;
  const recentBillings = billingListQuery.data ?? [];

  const pastDueSince = subscription?.pastDueSince
    ? new Date(subscription.pastDueSince)
    : undefined;
  const graceEndsAt = pastDueSince
    ? new Date(
        pastDueSince.getTime() + SUBSCRIPTION_CONFIG.graceDays * 24 * 60 * 60 * 1000,
      )
    : undefined;

  /**
   * View-mode resolution must NOT redirect to `/billing/plan` until we
   * definitively know the backend returned `null` (= no Subscription
   * doc, implicit free tier). Two failure modes to guard against:
   *
   * 1. On a hard refresh, `subscriptionQuery.isLoading` can be `false`
   *    while `data` is still `undefined` (TanStack v5 ties `isLoading`
   *    to `isFetching` — a transient error or paused fetch leaves
   *    `data === undefined` with `isLoading === false`). Coalescing
   *    `undefined` to `null` here would flip `viewMode` to `'landing'`
   *    and synchronously fire `<Navigate to="/billing/plan" replace />`
   *    before the retry completes — the user reported this as
   *    "refreshing `/billing` sends me to `/billing/plan`".
   *
   * 2. On an outright fetch error, we'd rather hold on the skeleton
   *    (TanStack will retry per the entity-config `retry`) than punt
   *    the user to the plan picker.
   *
   * Distinguishing these two: `subscription === null` is "backend said
   * null", `subscription === undefined` (via `subscriptionData`) is
   * "no answer yet".
   */
  let viewMode: BillingOverviewViewMode;
  if (
    subscriptionQuery.isPending ||
    subscriptionData === undefined ||
    subscriptionQuery.isError
  ) {
    viewMode = 'loading';
  } else if (subscriptionData === null) {
    viewMode = 'landing';
  } else if (features?.isBlocked) {
    viewMode = 'paywall';
  } else {
    viewMode = 'overview';
  }

  const payNow = () => {
    // `payBilling` just opens the existing `Billing.checkout.url` in
    // a new tab — no backend round-trip, no async state. We DO NOT
    // refetch list/subscription here: the user is now on Moyasar's
    // page in a separate tab. `refetchOnWindowFocus` will pick up
    // the new status when they tab back, and the webhook reconciles
    // the source of truth on the server side.
    payLatestUnpaid(recentBillings, { openIn: 'tab' });
  };

  return {
    subscriptionQuery,
    featuresQuery,
    billingListQuery,
    subscription,
    features,
    recentBillings,
    viewMode,
    graceEndsAt,
    isLoading:
      subscriptionQuery.isLoading ||
      featuresQuery.isLoading ||
      billingListQuery.isLoading,

    navigateToPlans: () => navigate('billing_change_plan'),
    navigateToAddons: () => navigate('billing_addons'),
    navigateToHistory: () => navigate('billing_history'),
    navigateToBilling: (id: string) => navigate('billing_invoice', { id }),
    payNow,
  };
}
